import { STORAGE_KEYS, STORAGE_SCOPE } from './keys'

/**
 * SSR-safe, error-swallowing wrappers around `window.localStorage`. Access can
 * throw (private mode, disabled storage, quota) and `window` is absent during
 * SSR, so every access is guarded once here instead of repeating `try/catch` at
 * each call site.
 */
function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

export function getString(key: string): string | undefined {
  try {
    return getStorage()?.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

export function setString(key: string, value: string): void {
  try {
    getStorage()?.setItem(key, value)
  } catch {
    // ignore — storage unavailable or over quota
  }
}

export function removeItem(key: string): void {
  try {
    getStorage()?.removeItem(key)
  } catch {
    // ignore
  }
}

export function getJSON<T>(key: string): T | undefined {
  const raw = getString(key)
  if (raw === undefined) return undefined
  try {
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

export function setJSON<T>(key: string, value: T): void {
  try {
    setString(key, JSON.stringify(value))
  } catch {
    // ignore — value not serializable or storage unavailable
  }
}

/**
 * Remove every `SESSION`-scoped key. Called at the session boundary (logout /
 * account switch) so the next login starts from a clean slate and never reuses
 * the previous account's auth tokens or telnyx WebRTC token.
 *
 * Device-scoped keys (table layout, sidebar, theme) are intentionally left
 * untouched — they belong to the device, not the account.
 *
 * The telnyx token in particular lives under a fixed, NON user-scoped key; if it
 * survived a logout the next account would reuse the previous account's still-
 * valid token and register as the wrong identity (inbound calls would ring the
 * old registration). Clearing it here forces a fresh, correctly-scoped fetch on
 * the next login.
 */
export function clearSessionStorage(): void {
  for (const def of Object.values(STORAGE_KEYS)) {
    if (def.scope === STORAGE_SCOPE.SESSION) removeItem(def.key)
  }
}
